/**
 * Utility functions for the Hybrid Documentation Crawler.
 * Phase 4: Frontend UI Components
 */

import { CrawlResult, PageGroup } from "@/types/crawl";

/**
 * Group discovered pages by URL path structure.
 * Groups by first path segment (e.g., /docs/api/endpoints → "docs")
 * Sorts groups by average confidence (highest first).
 *
 * @param pages Array of crawl results to group
 * @returns Array of page groups sorted by confidence
 */
export function groupPagesByPath(pages: CrawlResult[]): PageGroup[] {
  const groups = new Map<string, CrawlResult[]>();

  for (const page of pages) {
    try {
      const url = new URL(page.url);
      const pathParts = url.pathname.split("/").filter((p) => p.length > 0);

      // Use first path segment as group name (e.g., /docs/api/endpoints → "docs")
      // If no path segments, use "root"
      const firstSegment = pathParts[0] || "root";

      // Capitalize and format group name (e.g., "api-reference" → "Api Reference")
      const groupName = formatGroupName(firstSegment);

      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)!.push(page);
    } catch (error) {
      // Invalid URL, skip this page
      console.warn(`Invalid URL in crawl results: ${page.url}`, error);
      continue;
    }
  }

  // Convert to PageGroup array with average confidence
  const pageGroups = Array.from(groups.entries()).map(([name, pages]) => ({
    name,
    pages,
    confidence_avg: calculateAverageConfidence(pages),
  }));

  // Sort by average confidence (highest first)
  return pageGroups.sort((a, b) => b.confidence_avg - a.confidence_avg);
}

/**
 * Format group name from URL path segment.
 * Capitalizes first letter, replaces hyphens/underscores with spaces.
 *
 * @param segment URL path segment (e.g., "api-reference", "getting_started")
 * @returns Formatted group name (e.g., "Api Reference", "Getting Started")
 */
function formatGroupName(segment: string): string {
  // Replace hyphens and underscores with spaces
  const withSpaces = segment.replace(/[-_]/g, " ");

  // Capitalize first letter of each word
  return withSpaces
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Calculate average confidence score for an array of pages.
 *
 * @param pages Array of crawl results
 * @returns Average confidence (0.0-1.0)
 */
function calculateAverageConfidence(pages: CrawlResult[]): number {
  if (pages.length === 0) return 0;
  const sum = pages.reduce((acc, page) => acc + page.confidence, 0);
  return sum / pages.length;
}

/**
 * Get confidence badge variant based on confidence score.
 * Green for ≥0.9, yellow for 0.7-0.9, default otherwise.
 *
 * @param confidence Confidence score (0.0-1.0)
 * @returns Badge variant string
 */
export function getConfidenceBadgeVariant(confidence: number): "default" | "secondary" | "outline" {
  if (confidence >= 0.9) return "default"; // Green (high confidence)
  if (confidence >= 0.7) return "secondary"; // Yellow (medium confidence)
  return "outline"; // Gray (low confidence)
}

/**
 * Format confidence score as percentage string.
 *
 * @param confidence Confidence score (0.0-1.0)
 * @returns Formatted percentage (e.g., "95%")
 */
export function formatConfidence(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

/**
 * Format crawl job status for display.
 *
 * @param status Job status enum
 * @returns Human-readable status string
 */
export function formatJobStatus(status: string): {
  label: string;
  variant: "default" | "secondary" | "destructive";
} {
  switch (status) {
    case "queued":
      return { label: "Queued", variant: "secondary" };
    case "crawling":
      return { label: "Crawling...", variant: "default" };
    case "filtering":
      return { label: "Filtering...", variant: "default" };
    case "complete":
      return { label: "Complete", variant: "default" };
    case "failed":
      return { label: "Failed", variant: "destructive" };
    case "cancelled":
      return { label: "Cancelled", variant: "secondary" };
    case "timeout":
      return { label: "Timeout", variant: "destructive" };
    default:
      return { label: status, variant: "secondary" };
  }
}
